import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ChooseFlightData } from 'src/app/flight/models/ChooseFlightData';
import { FlightsFormData } from 'src/app/flight/models/FlightsFormData';
import { RequestStatus } from 'src/app/models/RequestStatus';
import { Direction } from 'src/app/flight/models/Direction';
import { getItemFromSessionStorage, CHOOSE_FLIGHT_DATA, setItemInSessionStorage } from 'src/app/utils/SessionStorageUtil';
import { OTAAirScheduleResponse, OriginDestinationOption } from '../models/OTAAirScheduleResponse';
import { FlightDetailsPanelService } from '../flight-details-dialog/flight-details-dialog.component';

function InboundValidatorRequired(flightsFormData: FlightsFormData): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (flightsFormData.isRoundTrip() && !value) {
      return { required: true };
    }

    return null;
  }
}

@Component({
    selector: 'app-choose-flight',
    templateUrl: './choose-flight.component.html',
    styleUrls: ['./choose-flight.component.scss'],
    standalone: false
})
export class ChooseFlightComponent implements OnInit, AfterViewInit {

  Direction = Direction;

  /** Which option has its details open. A different state from the selected flight (the form value). */
  private readonly details = inject(FlightDetailsPanelService);

  @Input() direction: Direction;
  @Input() flightsFormData: FlightsFormData;
  @Input() flights: OTAAirScheduleResponse;
  @Input() form: FormGroup;
  @Input() proceeded: boolean;

  /** True while the search form is collapsed: only then "Alterar busca" makes sense. */
  @Input() searchCollapsed = false;

  /** Asks the search panel to open again. Does not run a new search. */
  @Output() editSearch = new EventEmitter<void>();

  @ViewChild('panelArrow') panelArrow: ElementRef;
  @ViewChild('panelBody') panelBody: ElementRef;

  filterDirectOnly = true;

  /** A selection restored from the previous visit should stay visible with the list collapsed. */
  private restoredSelection = false;

  get filteredFlights(): OriginDestinationOption[] {
    if (!this.flights?.destinationOptionList) return [];
    if (!this.filterDirectOnly) return this.flights.destinationOptionList;
    return this.flights.destinationOptionList.filter(option => option.flightSegments.length === 1);
  }

  onConnectingFlightsToggle(includeConnectingFlights: boolean): void {
    this.filterDirectOnly = !includeConnectingFlights;
  }

  ngOnInit(): void {
    // Ordenar los vuelos por departureDateTime antes de cualquier otra lógica
    this.sortFlightsByDepartureDateTime();

    const validatorsByDirection = new Map<Direction, ValidatorFn[]>([
      [Direction.OUTBOUND, [Validators.required]],
      [Direction.INBOUND, [InboundValidatorRequired(this.flightsFormData)]]
    ]);

    const validators: ValidatorFn[] = validatorsByDirection.get(this.direction) || [];
    this.form.addControl(this.direction.name, new FormControl('', validators));
    this.fillFormWithDataFromStorage();

    const reset = this.form.reset.bind(this.form);
    this.form.reset = () => {
      reset();
      this.openCollapseFlights();
    }
  }

  ngAfterViewInit(): void {
    if (this.restoredSelection) {
      this.closeCollapseFlights();
    }
  }

  sortFlightsByDepartureDateTime(): void {
    if (this.flights?.destinationOptionList) {
      this.flights.destinationOptionList.sort((a: OriginDestinationOption, b: OriginDestinationOption) => {
        const dateA = a.flightSegments.length > 0 
          ? new Date(a.flightSegments[0].departureDateTime).getTime() 
          : Infinity; 
        const dateB = b.flightSegments.length > 0 
          ? new Date(b.flightSegments[0].departureDateTime).getTime() 
          : Infinity; 
  
        return dateA - dateB;
      });
    }
  }

  selectCheckbox(event: any, flight: OriginDestinationOption): void {
    if (event.checked) {
      this.form.get(this.direction.name)?.setValue(flight);
      this.closeCollapseFlights();
    } else {
      this.form.get(this.direction.name)?.setValue(null);
      this.openCollapseFlights();
    }
    setItemInSessionStorage(CHOOSE_FLIGHT_DATA, this.form.getRawValue());
  }

  isCheckboxDisabled(option: OriginDestinationOption): boolean {
    return option.flightSegments.some(segment => segment.blocked);
  }

  checked(flight: OriginDestinationOption): boolean {
    return this.sameItinerary(flight, this.form.get(this.direction.name)?.value);
  }

  /** True while the details panel shows this option. Cleared when the panel closes. */
  viewingDetails(flight: OriginDestinationOption): boolean {
    return this.details.flight() === flight;
  }

  /**
   * Identity of an itinerary ignores seat counts and blocked flags, which change
   * between the snapshot stored before confirmation and the flights reloaded on return.
   */
  private sameItinerary(left?: OriginDestinationOption | null, right?: OriginDestinationOption | null): boolean {
    const leftSegments = left?.flightSegments ?? [];
    const rightSegments = right?.flightSegments ?? [];

    if (!leftSegments.length || leftSegments.length !== rightSegments.length) {
      return false;
    }

    return leftSegments.every((segment, index) =>
      segment.flightNumber === rightSegments[index].flightNumber
      && segment.originLocationCode === rightSegments[index].originLocationCode
      && segment.destinationLocationCode === rightSegments[index].destinationLocationCode
      && segment.departureDateTime === rightSegments[index].departureDateTime
    );
  }

  toggleCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.toggle('closed');
    this.panelArrow?.nativeElement.classList.toggle('closed');
  }

  closeCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.add('closed');
    this.panelArrow?.nativeElement.classList.add('closed');
  }

  openCollapseFlights(): void {
    this.panelBody?.nativeElement.classList.remove('closed');
    this.panelArrow?.nativeElement.classList.remove('closed');
  }

  fillFormWithDataFromStorage(): void {
    const chooseFlightData = getItemFromSessionStorage<ChooseFlightData>(CHOOSE_FLIGHT_DATA);
    const stored = chooseFlightData?.[this.direction.name as keyof ChooseFlightData] as OriginDestinationOption | undefined;

    if (!stored?.flightSegments?.length) {
      return;
    }

    const match = this.flights?.destinationOptionList?.find(option => this.sameItinerary(option, stored));

    if (match) {
      this.form.get(this.direction.name)?.setValue(match);
      this.restoredSelection = true;
    }
  }
}
